import { PreviewKind } from "./previewTypes";

const SNIFF_BYTES = 8192;
const JSON_FULL_PARSE_LIMIT = 5 * 1024 * 1024;

const startsWithBytes = (bytes: Uint8Array, signature: number[]): boolean => {
  if (bytes.length < signature.length) return false;
  for (let i = 0; i < signature.length; i++) {
    if (bytes[i] !== signature[i]) return false;
  }
  return true;
};

const matchesAt = (
  bytes: Uint8Array,
  offset: number,
  signature: number[],
): boolean => {
  if (bytes.length < offset + signature.length) return false;
  for (let i = 0; i < signature.length; i++) {
    if (bytes[offset + i] !== signature[i]) return false;
  }
  return true;
};

const tryParseJson = (text: string): boolean => {
  try {
    JSON.parse(text);
    return true;
  } catch {
    return false;
  }
};

const looksLikeText = (text: string): boolean => {
  if (!text) return false;
  let printable = 0;
  let total = 0;
  for (const ch of text) {
    total += 1;
    const code = ch.charCodeAt(0);
    if (
      code === 0x09 ||
      code === 0x0a ||
      code === 0x0d ||
      (code >= 0x20 && code < 0x7f) ||
      code >= 0xa0
    ) {
      printable += 1;
    }
    if (total >= 2048) break;
  }
  return total > 0 && printable / total > 0.92;
};

export async function sniffKind(blob: Blob): Promise<PreviewKind> {
  if (!blob || blob.size === 0) return "unknown";

  const headSlice = blob.slice(0, Math.min(SNIFF_BYTES, blob.size));
  const buffer = await headSlice.arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (startsWithBytes(bytes, [0x25, 0x50, 0x44, 0x46, 0x2d])) return "pdf";

  if (startsWithBytes(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a]))
    return "image";
  if (startsWithBytes(bytes, [0xff, 0xd8, 0xff])) return "image";
  if (
    startsWithBytes(bytes, [0x47, 0x49, 0x46, 0x38, 0x37, 0x61]) ||
    startsWithBytes(bytes, [0x47, 0x49, 0x46, 0x38, 0x39, 0x61])
  )
    return "image";
  if (
    startsWithBytes(bytes, [0x52, 0x49, 0x46, 0x46]) &&
    matchesAt(bytes, 8, [0x57, 0x45, 0x42, 0x50])
  )
    return "image";
  if (startsWithBytes(bytes, [0x42, 0x4d])) return "image";

  if (matchesAt(bytes, 4, [0x66, 0x74, 0x79, 0x70])) return "video";
  if (
    startsWithBytes(bytes, [0x1a, 0x45, 0xdf, 0xa3]) ||
    startsWithBytes(bytes, [0x4f, 0x67, 0x67, 0x53])
  ) {
    return "video";
  }

  if (
    startsWithBytes(bytes, [0x49, 0x44, 0x33]) ||
    (bytes.length >= 2 &&
      bytes[0] === 0xff &&
      (bytes[1] & 0xe0) === 0xe0)
  ) {
    return "audio";
  }
  if (
    startsWithBytes(bytes, [0x52, 0x49, 0x46, 0x46]) &&
    matchesAt(bytes, 8, [0x57, 0x41, 0x56, 0x45])
  ) {
    return "audio";
  }
  if (startsWithBytes(bytes, [0x66, 0x4c, 0x61, 0x43])) return "audio";

  if (startsWithBytes(bytes, [0x50, 0x4b, 0x03, 0x04])) return "zip";

  let text: string;
  try {
    text = new TextDecoder("utf-8", { fatal: false }).decode(buffer);
  } catch {
    return "unknown";
  }
  const trimmed = text.replace(/^\uFEFF/, "").trim();
  if (!trimmed) return "unknown";

  if (
    /^<svg[\s>]/i.test(trimmed) ||
    (/^<\?xml/i.test(trimmed) && /<svg[\s>]/i.test(trimmed))
  ) {
    return "image";
  }
  if (/^<!doctype\s+html/i.test(trimmed) || /^<html[\s>]/i.test(trimmed)) {
    return "html";
  }

  const firstChar = trimmed[0];
  if (firstChar === "{" || firstChar === "[") {
    if (blob.size <= JSON_FULL_PARSE_LIMIT) {
      try {
        const fullText = await blob.text();
        if (tryParseJson(fullText)) return "json";
      } catch (_e) {
        void _e;
      }
    } else if (tryParseJson(trimmed)) {
      return "json";
    }
  }

  if (looksLikeText(text)) return "text";

  return "unknown";
}
